import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Play, Pause, MoreHorizontal, Trash2 } from 'lucide-react';
import StatusBadge from './StatusBadge';

function formatRelativo(dataIso) {
  if (!dataIso) return '';
  const d = new Date(dataIso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `há ${Math.max(1, Math.floor(diff))}s`;
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `há ${Math.floor(diff / 86400)} dia${Math.floor(diff / 86400) > 1 ? 's' : ''}`;
  return d.toLocaleDateString('pt-BR');
}

export default function CampaignRow({ c, onOpen, onDelete, onToggleStatus }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const handleClick = () => {
    if (onOpen) onOpen(c);
    else navigate(`/automacoes/${c.id}/editar`);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (!confirm(`Excluir a campanha "${c.nome}"? Essa ação não pode ser desfeita.`)) return;
    onDelete?.(c);
  };

  const conv = Number(c.taxa_conversao || 0);
  const enviadas = Number(c.total_enviadas || 0);
  const leads = Number(c.leads_ativos || 0);
  const subtitulo = [c.pasta_nome, formatRelativo(c.atualizado_em)].filter(Boolean).join(' · ');

  return (
    <div
      onClick={handleClick}
      className="grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-xl bg-surface-1/50 border border-white/[0.04] hover:border-white/[0.06] hover:bg-surface-1 cursor-pointer transition-all group"
    >
      <div className="col-span-5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-surface-2 grid place-items-center group-hover:bg-surface-3">
          <Zap className="w-4 h-4 text-emerald-400" strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-sm text-ink-1 font-medium">{c.nome}</div>
          {subtitulo && <div className="text-xs text-ink-4 mt-0.5">{subtitulo}</div>}
        </div>
      </div>

      <div className="col-span-1">
        <StatusBadge status={c.status} />
      </div>

      <div className="col-span-2 text-sm">
        <div className="text-ink-1 font-semibold">{leads}</div>
        <div className="text-[11px] text-ink-4">na sequência</div>
      </div>

      <div className="col-span-1 text-sm">
        <div className="text-ink-1 font-semibold">{enviadas.toLocaleString('pt-BR')}</div>
        <div className="text-[11px] text-ink-4">enviadas</div>
      </div>

      <div className="col-span-2 text-sm">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-surface-2 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, conv)}%` }} />
          </div>
          <span className="text-ink-1 font-semibold tabular-nums w-10 text-right">{conv}%</span>
        </div>
        <div className="text-[11px] text-ink-4 mt-1">conversão</div>
      </div>

      <div className="col-span-1 flex items-center justify-end gap-1">
        <button
          className={`p-1.5 rounded hover:bg-surface-2 ${c.status === 'ativa' ? 'text-emerald-400 hover:text-amber-300' : 'text-ink-4 hover:text-emerald-300'}`}
          title={c.status === 'ativa' ? 'Pausar automação' : 'Ativar automação'}
          onClick={(e) => { e.stopPropagation(); onToggleStatus?.(c); }}
        >
          {c.status === 'ativa' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <div className="relative" ref={menuRef}>
          <button
            className="p-1.5 rounded hover:bg-surface-2 text-ink-4 hover:text-ink-2"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-20 min-w-[160px] rounded-lg bg-surface-1 border border-white/[0.06] shadow-xl py-1"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleDelete}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5" /> Excluir
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
